import {
    faArrowDownLeftAndArrowUpRightToCenter,
    faArrowRotateRight,
    faArrowsRotate,
    faArrowUpRightAndArrowDownLeftFromCenter,
    faInfoCircle,
    faPaperPlane,
    faXmark,
} from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Button, Popup, PopupProps } from '@kcftech/design-system';
import { useMutation } from '@tanstack/react-query';
import RobotSVG from 'assets/Robot.svg';
import { FeedbackType } from 'components/chatbot/FeedbackModal';
import { Messages } from 'components/chatbot/Messages';
import {
    BottomDiv,
    BubbleContainer,
    chatbotPopupZIndex,
    Container,
    HeaderButton,
    InfoHint,
    RegenContainer,
    RegenText,
    SendButton,
    TextBoxContainer,
    TextInput,
    TextInputContainer,
    TitleBar,
    TitleText,
    ToolTip,
} from 'components/chatbot/styled';
import { HelpBotPayload, MessageInfo } from 'components/chatbot/types';
import { usePendoTrackChatbot } from 'components/chatbot/usePendoTrackChatbot';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as React from 'react';
import { HoverTooltip } from 'shared/hover_tooltip/HoverTooltip';
import { firstItem, lastItem, skipLast, takeLast } from 'utils/array';
import { fetchGatewayWithAuth } from 'utils/fetch';
import { HttpMethod } from 'utils/types';
import { v4 } from 'uuid';

const MaxMessageHistory = 50;
const ChatbotLocalStorageKey = 'chatbot.history';

const buildWelcomeMessage: () => MessageInfo = () => ({
    id: v4(),
    role: 'assistant',
    content:
        "Hello! I'm KCFchat, a helpful AI assistant here to answer your questions. What can I help you with today?",
    timestamp: new Date().getTime(),
    isEligible: false,
    sendToServer: false,
});

type SendMessageOptions = Readonly<{
    message: MessageInfo;
    messageHistory: MessageInfo[];
}>;

async function postHelpbot(payload: { messages: HelpBotPayload[] }): Promise<string> {
    try {
        const path = 'v4/helpbot';

        const response = await fetchGatewayWithAuth({
            path,
            method: HttpMethod.Post,
            payload,
        });

        const parsed = await response.json();

        return typeof parsed === 'string'
            ? parsed
            : "Sorry, I wasn't able to process your request. Please try again.";
    } catch {
        return "Sorry, I wasn't able to process your request. Please try again.";
    }
}

export type ChatbotProps = Readonly<{
    isFooterExpanded: boolean;
    isDrawerExpanded: boolean;
    onClose: () => void;
}>;

export function Chatbot({ isFooterExpanded, isDrawerExpanded, onClose }: ChatbotProps) {
    const trackChatbot = usePendoTrackChatbot();
    const inputRef = useRef<HTMLTextAreaElement>(null);
    const bubblesRef = useRef<HTMLDivElement>(null);
    const popupRef = useRef<HTMLDivElement>(null);
    const resetElemRef = useRef<HTMLButtonElement>(null);
    const expandElemRef = useRef<HTMLButtonElement>(null);
    const closeElemRef = useRef<HTMLButtonElement>(null);
    const [expanded, setExpanded] = useState(false);
    const [messages, setMessages] = useState<MessageInfo[]>([]);
    const [inputValue, setInputValue] = useState('');
    const [showExpandHint, setShowExpandHint] = useState(false);
    const [showResetHint, setShowResetHint] = useState(false);
    const [showCloseHint, setShowCloseHint] = useState(false);

    const resetChatBot = useCallback(() => {
        localStorage.removeItem(ChatbotLocalStorageKey);
        setMessages([buildWelcomeMessage()]);
        setTimeout(() => {
            if (inputRef.current) {
                inputRef.current.focus();
            }
        }, 0);
        trackChatbot({ type: 'Reset' });
    }, [trackChatbot]);

    const { mutate: initialize, isPending: isInitializing } = useMutation<MessageInfo[]>({
        mutationKey: ['chatbot-initialize'],
        mutationFn: () => {
            const savedMessages = localStorage.getItem(ChatbotLocalStorageKey) || '[]';
            const parsedMessages = JSON.parse(savedMessages) as MessageInfo[];

            const result: MessageInfo[] =
                parsedMessages.length > 0 ? parsedMessages : [buildWelcomeMessage()];

            return Promise.resolve(result);
        },

        onSuccess: setMessages,
        onError: resetChatBot,
    });

    const { mutate: sendMessage, isPending: isSendingPayload } = useMutation({
        mutationFn: async (options: SendMessageOptions) => {
            const newMessageList = [...options.messageHistory, options.message];
            setMessages(newMessageList);

            const validMessages = newMessageList.filter((msg) => msg.sendToServer);

            const last30Messages = takeLast(validMessages, 30);
            const firstUserIndex = last30Messages.findIndex((msg) => msg.role === 'user');
            const finalMessages = last30Messages.slice(firstUserIndex);

            if (firstItem(finalMessages)?.role !== 'user') {
                return undefined;
            }

            const finalPayload = finalMessages.map<HelpBotPayload>((msg) => ({
                role: msg.role,
                content: msg.content,
                timestamp: new Date(msg.timestamp),
                feedback:
                    msg.feedback !== undefined
                        ? {
                              eligible: true,
                              negative: msg.feedback === 'negative',
                              positive: msg.feedback === 'positive',
                          }
                        : undefined,
            }));

            return postHelpbot({ messages: finalPayload });
        },

        onSuccess: (responseContent) => {
            if (!responseContent) {
                return;
            }

            const responseMessage: MessageInfo = {
                id: v4(),
                role: 'assistant',
                content: responseContent,
                timestamp: new Date().getTime(),
                isEligible: true,
                sendToServer: true,
            };

            setMessages((current) => [...current, responseMessage]);
        },

        onError: (error) => {
            const responseError: MessageInfo = {
                id: v4(),
                role: 'assistant',
                content: `${error}`,
                timestamp: new Date().getTime(),
                isEligible: false,
                sendToServer: true,
            };

            setMessages((current) => [...current, responseError]);
        },
    });

    const handleSendMessage = () => {
        if (!inputValue.trim()) {
            return;
        }

        const newMessage: MessageInfo = {
            id: v4(),
            role: 'user',
            content: inputValue,
            timestamp: new Date().getTime(),
            isEligible: false,
            sendToServer: true,
        };

        trackChatbot({ type: 'Query', query: newMessage.content });
        sendMessage({ message: newMessage, messageHistory: messages });

        setInputValue('');
    };

    const showRegenerateResponse = useMemo(() => {
        const lastMessage = lastItem(messages);
        return (
            lastMessage && lastMessage.role === 'assistant' && lastMessage.sendToServer
        );
    }, [messages]);

    const handleInputChange = (event: {
        target: { value: React.SetStateAction<string> };
    }) => {
        setInputValue(event.target.value);
        if (
            inputRef.current &&
            inputRef.current.scrollHeight > inputRef.current.clientHeight
        ) {
            inputRef.current.style.height = `${inputRef.current.scrollHeight}px`;
        }
    };

    const handleRegenerateResponse = () => {
        const lastAssistantMessage = lastItem(messages);
        if (
            !lastAssistantMessage ||
            lastAssistantMessage.role !== 'assistant' ||
            !lastAssistantMessage.sendToServer
        ) {
            return;
        }
        const messageToResend = lastItem(skipLast(messages, 1));
        if (
            !messageToResend ||
            messageToResend.role !== 'user' ||
            !messageToResend.sendToServer
        ) {
            return;
        }
        trackChatbot({ type: 'Regenerate', query: messageToResend.content });
        sendMessage({ message: messageToResend, messageHistory: skipLast(messages, 2) });
    };

    const scrollToBottom = () => {
        if (bubblesRef.current) {
            bubblesRef.current.scrollIntoView({
                behavior: 'smooth',
                block: 'end',
                inline: 'nearest',
            });
            bubblesRef.current.scrollTop = bubblesRef.current.scrollHeight;
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            if (!isLoading) {
                handleSendMessage();
                if (inputRef.current) {
                    inputRef.current.value = '';
                    inputRef.current.style.height = 'auto';
                }
            }
        }
    };

    const onFeedback = (message: MessageInfo, feedback: FeedbackType) => {
        const newMessage: MessageInfo = {
            id: v4(),
            role: 'assistant',
            content:
                'Your additional feedback was received. Thank you! Is there anything else I can help you with?',
            timestamp: new Date().getTime(),
            isEligible: false,
            sendToServer: false,
        };
        if (lastItem(messages)?.role === 'assistant' && !lastItem(messages)?.isEligible) {
            setMessages((current) => [
                ...current.map((msg) => {
                    if (msg === lastItem(messages)) {
                        return newMessage;
                    }
                    if (msg === message) {
                        return { ...msg, feedback };
                    }
                    return msg;
                }),
            ]);
        } else {
            setMessages((current) => [
                ...current.map((msg) => (msg === message ? { ...msg, feedback } : msg)),
                newMessage,
            ]);
        }
    };

    const isLoading = isInitializing || isSendingPayload;

    useEffect(() => {
        initialize();
        trackChatbot({ type: 'Initialized' });
        return () => trackChatbot({ type: 'Dismissed' });
    }, [initialize, trackChatbot]);

    useEffect(() => {
        scrollToBottom();

        if (messages.length > 0) {
            localStorage.setItem(
                ChatbotLocalStorageKey,
                JSON.stringify(takeLast(messages, MaxMessageHistory))
            );
        }
    }, [messages]);

    return (
        <Container>
            <TextBoxContainer
                $expanded={expanded}
                $drawerExpanded={isDrawerExpanded}
                $footerExpanded={isFooterExpanded}
            >
                <TitleBar>
                    <img
                        src={RobotSVG}
                        alt="KCFchat Icon"
                        style={{
                            height: '5rem',
                            width: '5rem',
                            justifyContent: 'center',
                            marginLeft: '2.5rem',
                        }}
                    />
                    <TitleText>KCFchat</TitleText>
                    <InfoHint ref={popupRef}>
                        <FontAwesomeIcon icon={faInfoCircle} size="sm" />
                    </InfoHint>
                    <HoverTooltip anchorRef={popupRef}>
                        <div style={{ maxWidth: '30rem' }}>
                            KCFchat is an experimental chatbot and generated responses may
                            not always be 100% accurate.
                        </div>
                    </HoverTooltip>
                    <HeaderButton
                        onClick={resetChatBot}
                        onMouseEnter={() => setShowResetHint(true)}
                        onMouseLeave={() => setShowResetHint(false)}
                        style={{ marginRight: '6rem' }}
                        ref={resetElemRef}
                    >
                        <FontAwesomeIcon icon={faArrowRotateRight} />
                    </HeaderButton>
                    <HeaderButton
                        onClick={() => {
                            setExpanded(!expanded);
                            trackChatbot({ type: !expanded ? 'Expanded' : 'Collapsed' });
                        }}
                        onMouseEnter={() => setShowExpandHint(true)}
                        onMouseLeave={() => setShowExpandHint(false)}
                        style={{ marginRight: '3rem' }}
                        ref={expandElemRef}
                    >
                        <FontAwesomeIcon
                            icon={
                                expanded
                                    ? faArrowDownLeftAndArrowUpRightToCenter
                                    : faArrowUpRightAndArrowDownLeftFromCenter
                            }
                        />
                    </HeaderButton>
                    <HeaderButton
                        onClick={onClose}
                        onMouseEnter={() => setShowCloseHint(true)}
                        onMouseLeave={() => setShowCloseHint(false)}
                        ref={closeElemRef}
                    >
                        <FontAwesomeIcon icon={faXmark} size="lg" />
                    </HeaderButton>
                </TitleBar>
                <BubbleContainer ref={bubblesRef}>
                    {showRegenerateResponse &&
                        messages.length > 1 &&
                        (!isLoading ? (
                            <RegenContainer>
                                <Button
                                    appearance="secondary"
                                    spacing="compact"
                                    onClick={handleRegenerateResponse}
                                    disabled={isLoading}
                                >
                                    <div className="flex align-center gap-10">
                                        <RegenText>Regenerate Response</RegenText>
                                        <FontAwesomeIcon icon={faArrowsRotate} />
                                    </div>
                                </Button>
                            </RegenContainer>
                        ) : (
                            <div />
                        ))}
                    <Messages
                        messages={messages}
                        loading={isLoading}
                        onFeedback={onFeedback}
                    />
                </BubbleContainer>
                <BottomDiv />
                <TextInputContainer>
                    <TextInput
                        ref={inputRef}
                        value={inputValue}
                        onChange={handleInputChange}
                        placeholder="Add your message here"
                        onKeyDown={handleKeyDown}
                        maxLength={2048}
                        rows={1}
                        autoFocus
                    />
                    <SendButton
                        onClick={() => handleSendMessage()}
                        disabled={isLoading || !inputValue.trim()}
                    >
                        <FontAwesomeIcon icon={faPaperPlane} className="f-16" />
                    </SendButton>
                </TextInputContainer>
            </TextBoxContainer>
            {showResetHint && (
                <HeaderPopup targetRef={resetElemRef}>
                    <ToolTip>Reset Conversation</ToolTip>
                </HeaderPopup>
            )}
            {showExpandHint && (
                <HeaderPopup targetRef={expandElemRef}>
                    <ToolTip>{expanded ? 'Collapse' : 'Expand'}</ToolTip>
                </HeaderPopup>
            )}
            {showCloseHint && (
                <HeaderPopup targetRef={closeElemRef}>
                    <ToolTip>Close</ToolTip>
                </HeaderPopup>
            )}
        </Container>
    );
}

function HeaderPopup({
    targetRef,
    children,
}: Pick<PopupProps, 'targetRef'> & { children: React.ReactNode }) {
    return (
        <Popup targetRef={targetRef} style={{ zIndex: chatbotPopupZIndex }}>
            {children}
        </Popup>
    );
}
